// https://www.radix-ui.com/primitives/docs/components/accordion
import React, { FC, useState, useEffect } from 'react';
import { Accordion } from 'radix-ui';
import cn from 'classnames';
import { IconArrow } from '@base/icon';
import { Link } from '@base/lib/Link';

export type CollapseProps = {
    list: ICollapseItem[];
    accordion?: boolean; //	手风琴模式
    collapsible?: boolean; // 是否允许折叠
    value?: string[];
    defaultValue?: string[];
    onChange?: (value: string[]) => void;
    className?: string;
};

export type ICollapseItem = {
    key: string;
    title: React.ReactNode;
    extra?: React.ReactNode;
    expandIcon?: boolean | React.ReactNode | ((isActive: boolean) => React.ReactNode);
    expandIconPosition?: 'left' | 'right';
    triggerOnlyOnIcon?: boolean; //仅在点击 expandIcon 时触发面板开合
    headerProps?: {
        href?: string;
        target?: HTMLAnchorElement['target'];
        className?: string;
        onClick?: React.MouseEventHandler<HTMLDivElement>;
    };
    body?: false | React.ReactNode;
    disabled?: boolean;
    className?: string;
    style?: React.CSSProperties;
}

function getHeaderClass(item: Omit<ICollapseItem, 'key'>): string {
    const {
        expandIcon,
        expandIconPosition = 'right',
        triggerOnlyOnIcon = false,
        disabled = false,
        body,
    } = item;

    const arr = ['sw-collapse-header user-select-none'];
    if (expandIcon !== false) {
        arr.push('flex flex-middle');
        if (expandIconPosition === 'left') {
            arr.push('flex-reverse');
        }
    }
    if (!!body && !triggerOnlyOnIcon && !disabled) {
        arr.push('pointer');
    }
    return arr.join(' ');
}

export const Collapse: FC<CollapseProps> = (props) => {
    const {
        list,
        accordion = false,
        collapsible = true,
        value: propsValue,
        defaultValue,
        onChange,
        className,
    } = props;

    const [value, setValue] = useState<string[]>(defaultValue || []);

    const handleChange = (newValue: string | (string[])) => {
        const arr = Array.isArray(newValue)
            ? newValue
            : (!!newValue ? [newValue] : []);
        if (!propsValue) {
            setValue(arr);
        }
        onChange?.(arr);
    }

    useEffect(() => {
        if (!!propsValue) {
            setValue(propsValue);
        }
    }, [propsValue]);

    return (
        // @ts-ignore
        <Accordion.Root
            className={cn(className, 'sw-collapse')}
            type={accordion ? 'single' : 'multiple'}
            collapsible={accordion ? collapsible : void 0}
            value={accordion ? (value[0] || '') : value}
            onValueChange={handleChange}
        >
            {list.map(({ key, ...item }) => {
                const {
                    triggerOnlyOnIcon = false,
                    headerProps,
                    body,
                    className,
                    style,
                } = item;

                const {
                    href,
                    target,
                    className: extraHeaderClass,
                    ...headerRest
                } = headerProps || {};

                const headerClass = getHeaderClass(item);
                const hasBody = !!body;
                const isActive = value.includes(key);

                return (
                    <Accordion.Item
                        className={cn(
                            className,
                            'sw-collapse-item',
                            { 'sw-collapse-item-active': isActive }
                        )}
                        style={style}
                        key={key}
                        value={key}
                    >
                        <AccordionHeader isHeader={hasBody && !triggerOnlyOnIcon}>
                            <AccordionTrigger isTrigger={hasBody && !triggerOnlyOnIcon}>
                                {!!href ? (
                                    <Link
                                        className={cn(headerClass, extraHeaderClass, 'block')}
                                        href={href}
                                        target={target}
                                    >
                                        <HeaderMain {...item} isActive={isActive}/>
                                    </Link>
                                ) : (
                                    <div
                                        className={cn(headerClass, extraHeaderClass)}
                                        {...headerRest}
                                    >
                                        <HeaderMain {...item} isActive={isActive}/>
                                    </div>
                                )}
                            </AccordionTrigger>
                        </AccordionHeader>

                        {!!body && (
                            <Accordion.Content className={'sw-collapse-body-wrap'}>
                                <div className={'sw-collapse-body'}>
                                    {body}
                                </div>
                            </Accordion.Content>
                        )}
                    </Accordion.Item>
                )
            })}
        </Accordion.Root>
    )
}

const AccordionHeader: FC<{ isHeader: boolean; children: React.ReactElement; }> = ({ isHeader, children }) => {
    return isHeader ? (
        <Accordion.Header asChild>
            {children}
        </Accordion.Header>
    ) : children
};

const AccordionTrigger: FC<{ isTrigger: boolean; children: React.ReactElement; }> = ({ isTrigger, children }) => {
    return isTrigger ? (
        <Accordion.Trigger asChild>
            {children}
        </Accordion.Trigger>
    ) : children
};

export const HeaderMain: FC<Omit<ICollapseItem, 'key'> & { isActive: boolean }> = props => {
    const {
        title,
        extra,
        expandIcon = true,
        triggerOnlyOnIcon = false,
        disabled = false,
        isActive,
        body,
    } = props;

    return (
        <>
            {!!extra && (
                <div className={'sw-collapse-extra'}>
                    {extra}
                </div>
            )}

            <div className={cn('sw-collapse-title', { 'flex-1': expandIcon !== false })}>
                {title}
            </div>

            {expandIcon !== false && (
                <AccordionTrigger isTrigger={!!body && triggerOnlyOnIcon && !disabled}>
                    <div className={cn(
                        'sw-collapse-trigger user-select-none low-line',
                        { pointer: triggerOnlyOnIcon && !disabled }
                    )}>
                        {typeof expandIcon === 'function'
                            ? expandIcon(isActive)
                            : (expandIcon === true ? (
                                    <IconArrow
                                        direction={isActive ? 'up' : 'down'}
                                        className={'sw-collapse-arrow'}
                                    />
                                ) : expandIcon
                            )
                        }
                    </div>
                </AccordionTrigger>
            )}
        </>
    )
};